import { useState, useEffect, useCallback } from 'react';
import { apiGet, apiPost } from '@/utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowLeft, 
  Bell,
  Mail,
  Volume2,
  Clock,
  Plane,
  AlertTriangle,
  Save,
  RefreshCw,
  Shield,
  Key,
  Link,
  Copy,
  Loader2,
  Eye,
  EyeOff,
  User,
  Database,
  Info,
  HardDrive,
  Image,
  Archive,
  Zap,
  CheckCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';


// Storage Management Component
const StorageManagementCard = ({ saving, setSaving }) => {
  const [storageStats, setStorageStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [compressionResults, setCompressionResults] = useState(null);
  const [compressing, setCompressing] = useState(false);

  const loadStorageStats = async () => {
    setLoadingStats(true);
    try {
      const stats = await apiGet('/api/storage/stats');
      setStorageStats(stats);
    } catch (error) {
      toast.error('فشل تحميل إحصائيات التخزين / Failed to load storage stats');
    } finally {
      setLoadingStats(false);
    }
  };

  useEffect(() => {
    loadStorageStats();
  }, []);

  const handleCompressPreview = async () => {
    setCompressing(true);
    try {
      // Dry run first to show potential savings
      const results = await apiPost('/api/storage/compress-images?dry_run=true&max_size_kb=200', {});
      setCompressionResults(results);
      
      if (results.savings_mb > 0) {
        toast.success(`يمكن توفير ${results.savings_mb} MB من المساحة!`);
      } else {
        toast.info('جميع الصور محسنة بالفعل!');
      }
    } catch (error) {
      toast.error('فشل تحليل الصور / Failed to analyze images');
    } finally {
      setCompressing(false);
    }
  };

  const handleCompressExecute = async () => {
    if (!window.confirm('⚠️ تحذير: سيتم ضغط الصور الكبيرة لتوفير المساحة.\n\nالصور ستبقى واضحة ولن تُحذف.\n\nهل تريد المتابعة؟\n\nWarning: Large images will be compressed to save space. Images will remain clear and won\'t be deleted. Continue?')) {
      return;
    }
    
    setCompressing(true);
    try {
      const results = await apiPost('/api/storage/compress-images?dry_run=false&max_size_kb=200', {});
      setCompressionResults(results);
      toast.success(`✅ تم ضغط ${results.workers_compressed + results.visas_compressed} صورة! وفّرت ${results.savings_mb} MB`);
      // Reload stats
      loadStorageStats();
    } catch (error) {
      toast.error('فشل ضغط الصور / Failed to compress images');
    } finally {
      setCompressing(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Storage Overview Card */}
      <Card className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
        <CardHeader className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
          <CardTitle className="flex items-center gap-2">
            <HardDrive className="h-5 w-5" />
            📊 إحصائيات التخزين / Storage Statistics
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          {loadingStats ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
            </div>
          ) : storageStats ? (
            <div className="space-y-6">
              {/* Total Size */}
              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-emerald-700 font-medium">إجمالي حجم الصور</p>
                    <p className="text-3xl font-bold text-emerald-800">{storageStats.total_size_mb} MB</p>
                  </div>
                  <div className="w-16 h-16 rounded-full bg-emerald-500 flex items-center justify-center">
                    <Database className="h-8 w-8 text-white" />
                  </div>
                </div>
                {storageStats.potential_savings_mb > 0 && (
                  <div className="mt-3 flex items-center gap-2 text-amber-600">
                    <Zap className="h-4 w-4" />
                    <span className="text-sm">يمكن توفير ~{storageStats.potential_savings_mb} MB بالضغط</span>
                  </div>
                )}
              </div>

              {/* Breakdown */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
                  <User className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-blue-800">{storageStats.workers?.images_count || 0}</p>
                  <p className="text-xs text-blue-600">صور العمال</p>
                  <p className="text-sm text-blue-500 mt-1">{Math.round(storageStats.workers?.total_size_kb / 1024 || 0)} MB</p>
                </div>
                <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 text-center">
                  <Image className="h-6 w-6 text-purple-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-purple-800">{storageStats.visas?.images_count || 0}</p>
                  <p className="text-xs text-purple-600">صور التأشيرات</p>
                  <p className="text-sm text-purple-500 mt-1">{Math.round(storageStats.visas?.total_size_kb / 1024 || 0)} MB</p>
                </div>
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 text-center">
                  <Plane className="h-6 w-6 text-orange-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-orange-800">{storageStats.tickets?.images_count || 0}</p>
                  <p className="text-xs text-orange-600">ملفات التذاكر</p>
                  <p className="text-sm text-orange-500 mt-1">{Math.round(storageStats.tickets?.total_size_kb / 1024 || 0)} MB</p>
                </div>
              </div>

              {/* Large Images Warning */}
              {storageStats.images_over_200kb > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="font-semibold text-amber-800">
                      {storageStats.images_over_200kb} صورة كبيرة الحجم
                    </p>
                    <p className="text-sm text-amber-600">
                      هذه الصور أكبر من 200KB ويمكن ضغطها لتوفير المساحة دون فقدان الجودة
                    </p>
                  </div>
                </div>
              )}

              <Button onClick={loadStorageStats} variant="outline" className="w-full">
                <RefreshCw className="h-4 w-4 mr-2" />
                تحديث الإحصائيات
              </Button>
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              لا توجد بيانات متاحة
            </div>
          )}
        </CardContent>
      </Card>

      {/* Image Compression Card */}
      <Card className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
        <CardHeader className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5" />
            ⚡ ضغط الصور / Image Compression
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-blue-600 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-semibold mb-1">كيف يعمل ضغط الصور؟</p>
                <ul className="list-disc list-inside text-blue-700 space-y-1">
                  <li>يقلل حجم الصور الكبيرة (أكثر من 200KB) تلقائياً</li>
                  <li>يحافظ على جودة الصورة الواضحة</li>
                  <li>لا يحذف أي صور - فقط يقلل حجمها</li>
                  <li>الصور الصغيرة لن تتأثر</li>
                </ul>
              </div>
            </div>
          </div>

          {compressionResults && (
            <div className={`border rounded-xl p-4 ${compressionResults.dry_run ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'}`}>
              <div className="flex items-center gap-2 mb-3">
                {compressionResults.dry_run ? (
                  <AlertTriangle className="h-5 w-5 text-amber-600" />
                ) : (
                  <CheckCircle className="h-5 w-5 text-emerald-600" />
                )}
                <span className={`font-semibold ${compressionResults.dry_run ? 'text-amber-800' : 'text-emerald-800'}`}>
                  {compressionResults.dry_run ? 'معاينة النتائج' : 'تم الضغط بنجاح!'}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-600">صور العمال للضغط</p>
                  <p className="font-bold text-lg">{compressionResults.workers_compressed}</p>
                </div>
                <div>
                  <p className="text-slate-600">صور التأشيرات للضغط</p>
                  <p className="font-bold text-lg">{compressionResults.visas_compressed}</p>
                </div>
                <div>
                  <p className="text-slate-600">الحجم الأصلي</p>
                  <p className="font-bold text-lg">{compressionResults.original_size_mb} MB</p>
                </div>
                <div>
                  <p className="text-slate-600">الحجم الجديد</p>
                  <p className="font-bold text-lg text-emerald-600">{compressionResults.new_size_mb} MB</p>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-slate-200">
                <p className="text-center">
                  <span className="text-slate-600">التوفير: </span>
                  <span className="font-bold text-emerald-600 text-xl">{compressionResults.savings_mb} MB</span>
                </p>
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <Button 
              onClick={handleCompressPreview}
              disabled={compressing}
              variant="outline"
              className="flex-1"
            >
              {compressing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
              معاينة التوفير
            </Button>
            <Button 
              onClick={handleCompressExecute}
              disabled={compressing || !compressionResults || compressionResults.savings_mb === 0}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700"
            >
              {compressing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Zap className="h-4 w-4 mr-2" />}
              تنفيذ الضغط
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Archive Management Card */}
      <ArchiveManagementCard />
    </div>
  );
};


// Archive Management Card - real implementation (archive, list, restore)
const ArchiveManagementCard = () => {
  const [stats, setStats] = useState(null);
  const [list, setList] = useState(null);
  const [threshold, setThreshold] = useState(365);
  const [loading, setLoading] = useState(false);
  const [showList, setShowList] = useState(false);

  const loadStats = useCallback(async () => {
    try {
      const s = await apiGet('/api/storage/archive/stats');
      setStats(s);
    } catch (e) {
      toast.error('فشل تحميل إحصائيات الأرشيف');
    }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);

  const runPreview = async () => {
    setLoading(true);
    try {
      const r = await apiPost(`/api/storage/archive/archive-old?dry_run=true&threshold_days=${threshold}`, {});
      toast.info(`سيتم أرشفة ${r.matched} سجل`);
    } catch (e) {
      toast.error('فشل المعاينة');
    } finally { setLoading(false); }
  };

  const runArchive = async () => {
    if (!window.confirm(`⚠️ سيتم نقل السجلات المكتملة منذ أكثر من ${threshold} يوم إلى الأرشيف.\n\nيمكن استرجاعها في أي وقت من قائمة الأرشيف.\n\nمتابعة؟`)) return;
    setLoading(true);
    try {
      const r = await apiPost(`/api/storage/archive/archive-old?dry_run=false&threshold_days=${threshold}`, {});
      toast.success(`✅ تم أرشفة ${r.archived} سجل${r.failed > 0 ? ` (فشل ${r.failed})` : ''}`);
      await loadStats();
    } catch (e) {
      toast.error('فشل الأرشفة');
    } finally { setLoading(false); }
  };

  const loadList = async () => {
    setLoading(true);
    try {
      const r = await apiGet(`/api/storage/archive/list?page=1&page_size=50`);
      setList(r);
      setShowList(true);
    } catch (e) {
      toast.error('فشل تحميل القائمة');
    } finally { setLoading(false); }
  };

  const restoreCase = async (id, name) => {
    if (!window.confirm(`استرجاع ${name}؟`)) return;
    try {
      await apiPost(`/api/storage/archive/restore/${id}`, {});
      toast.success(`✅ تم استرجاع ${name}`);
      await loadList();
      await loadStats();
    } catch (e) {
      toast.error('فشل الاسترجاع');
    }
  };

  return (
    <Card className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm" data-testid="archive-card">
      <CardHeader className="bg-gradient-to-r from-slate-600 to-slate-700 text-white">
        <CardTitle className="flex items-center gap-2">
          <Archive className="h-5 w-5" />
          📦 الأرشفة / Archiving
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6 space-y-4">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-slate-700" data-testid="archive-total">{stats.total_cases}</p>
              <p className="text-xs text-slate-500 mt-1">إجمالي الحالات / Total</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-amber-700" data-testid="archive-eligible">{stats.archivable_cases}</p>
              <p className="text-xs text-amber-600 mt-1">قابلة للأرشفة / Archivable</p>
            </div>
            <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-indigo-700" data-testid="archive-archived">{stats.already_archived}</p>
              <p className="text-xs text-indigo-600 mt-1">مؤرشفة / Archived</p>
            </div>
          </div>
        )}

        {/* Threshold control */}
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
          <div>
            <Label className="text-sm text-slate-700 mb-1 block">عتبة الأرشفة / Archive Threshold (days)</Label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min="30"
                max="3650"
                value={threshold}
                onChange={(e) => setThreshold(Number(e.target.value) || 365)}
                className="max-w-[140px]"
                data-testid="archive-threshold-input"
              />
              <span className="text-xs text-slate-500">ستُؤرشف السجلات المكتملة الأقدم من هذا العدد من الأيام</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={runPreview} disabled={loading} variant="outline" className="flex-1 sm:flex-none" data-testid="archive-preview-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
              معاينة / Preview
            </Button>
            <Button onClick={runArchive} disabled={loading || !stats?.archivable_cases} className="flex-1 sm:flex-none bg-amber-600 hover:bg-amber-700" data-testid="archive-run-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Archive className="h-4 w-4 mr-2" />}
              تنفيذ الأرشفة / Archive Now
            </Button>
            <Button onClick={loadList} disabled={loading} variant="outline" className="flex-1 sm:flex-none" data-testid="archive-list-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Database className="h-4 w-4 mr-2" />}
              عرض الأرشيف / View Archive
            </Button>
          </div>
        </div>

        {/* Archive list */}
        {showList && list && (
          <div className="bg-white border border-slate-200 rounded-xl p-4 max-h-[320px] overflow-y-auto" data-testid="archive-list">
            <div className="flex items-center justify-between mb-3 sticky top-0 bg-white">
              <h4 className="font-semibold text-slate-700 text-sm">السجلات المؤرشفة ({list.total})</h4>
              <button onClick={() => setShowList(false)} className="text-xs text-slate-500 hover:text-slate-700">إغلاق ✕</button>
            </div>
            {list.items.length === 0 ? (
              <p className="text-center text-slate-400 text-sm py-6">لا توجد سجلات مؤرشفة</p>
            ) : (
              <div className="space-y-2">
                {list.items.map((item) => (
                  <div key={item.id} className="flex items-center justify-between gap-2 p-2 rounded-lg border border-slate-100 hover:bg-slate-50" data-testid={`archive-item-${item.id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-800 truncate">{item.workerName || '—'}</p>
                      <p className="text-xs text-slate-500">
                        {item.passportNumber || '—'} · {item.original_status || 'completed'}
                        {item.archived_at && ` · ${new Date(item.archived_at).toLocaleDateString('en-GB')}`}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => restoreCase(item.id, item.workerName || item.id)}
                      className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                      data-testid={`archive-restore-${item.id}`}
                    >
                      <RefreshCw className="h-3 w-3 mr-1" />
                      استرجاع
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Info */}
        <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-indigo-600 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-indigo-800 space-y-1">
              <p className="font-semibold">كيف تعمل الأرشفة؟</p>
              <ul className="list-disc list-inside space-y-0.5 text-indigo-700">
                <li>تنقل السجلات المكتملة (completed/settled/cancelled/closed) الأقدم من عتبة الأيام إلى مجموعة الأرشيف.</li>
                <li>البيانات تبقى محفوظة بالكامل ويمكن استرجاعها بالنقر على "استرجاع" أمام أي سجل.</li>
                <li>تخفف حمل قاعدة البيانات وتُسرّع الصفحات الرئيسية بدون فقدان أي معلومة.</li>
              </ul>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};


const SettingsPage = () => {
  const navigate = useNavigate();
  
  // Notification settings state
  const [notificationSettings, setNotificationSettings] = useState({
    admin_email: '',
    enabled_4_days: true,
    enabled_24_hours: true,
    enabled_2_hours: true,
    enabled_landed: true,
    enabled_delayed: true,
    delay_threshold_minutes: 30
  });
  
  // Sound settings (local storage)
  const [soundSettings, setSoundSettings] = useState({
    welcome_sound: false,
    notification_sound: false
  });
  
  // Visa Tracker PIN settings
  const [newPin, setNewPin] = useState('');
  const [showNewPin, setShowNewPin] = useState(false);
  
  // Loading states
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Load settings
  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      
      // Load notification settings
      const notifSettings = await apiGet('/api/settings/notifications');
      setNotificationSettings({
        admin_email: notifSettings.admin_email || '',
        enabled_4_days: notifSettings.enabled_4_days ?? true,
        enabled_24_hours: notifSettings.enabled_24_hours ?? true,
        enabled_2_hours: notifSettings.enabled_2_hours ?? true,
        enabled_landed: notifSettings.enabled_landed ?? true,
        enabled_delayed: notifSettings.enabled_delayed ?? true,
        delay_threshold_minutes: notifSettings.delay_threshold_minutes || 30
      });
      
      // Load sound settings from localStorage
      const savedSoundSettings = localStorage.getItem('soundSettings');
      if (savedSoundSettings) {
        setSoundSettings(JSON.parse(savedSoundSettings));
      }
      
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    loadSettings();
  }, [loadSettings]);
  
  // Save notification settings
  const saveNotificationSettings = async () => {
    setSaving(true);
    try {
      await apiPost('/api/settings/notifications', notificationSettings);
      toast.success('Notification settings saved!');
    } catch (error) {
      toast.error(error.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };
  
  // Save sound settings to localStorage
  const saveSoundSettings = () => {
    localStorage.setItem('soundSettings', JSON.stringify(soundSettings));
    toast.success('Sound preferences saved!');
  };
  
  // Change Visa Tracker PIN
  const handleChangePin = async () => {
    if (!newPin || newPin.length < 4) {
      toast.error('PIN must be at least 4 characters');
      return;
    }
    
    setSaving(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/visa-tracker/pin?pin=${encodeURIComponent(newPin)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to change PIN');
      }
      
      toast.success('PIN changed successfully! / تم تغيير الرمز بنجاح');
      setNewPin('');
    } catch (error) {
      toast.error(error.message || 'Failed to change PIN');
    } finally {
      setSaving(false);
    }
  };
  
  // Notification toggle component
  const NotificationToggle = ({ id, checked, onChange, title, description, icon: Icon }) => (
    <label className="flex items-center justify-between p-4 bg-slate-50 border border-slate-200 rounded-xl cursor-pointer hover:bg-slate-100 transition-all">
      <div className="flex items-center gap-3">
        {Icon && <Icon className="h-5 w-5 text-slate-500" />}
        <div>
          <p className="font-semibold text-slate-800">{title}</p>
          <p className="text-sm text-slate-500">{description}</p>
        </div>
      </div>
      <Switch
        id={id}
        checked={checked}
        onCheckedChange={onChange}
      />
    </label>
  );

  return (
    <div className="space-y-6 pb-20" data-testid="settings-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/dashboard')}
          className="h-11 w-11 rounded-full bg-blue-100 hover:bg-blue-200 text-blue-600"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-800 flex items-center gap-2">
            ⚙️ System Settings
          </h1>
          <p className="text-sm text-slate-500">Configure notifications, sounds, and security</p>
        </div>
      </div>
      
      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : (
        <Tabs defaultValue="notifications" className="w-full">
          <TabsList className="w-full bg-white border border-slate-200 rounded-xl p-1 grid grid-cols-4">
            <TabsTrigger value="notifications" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-lg text-xs sm:text-sm">
              <Bell className="h-4 w-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Notifications</span>
              <span className="sm:hidden">Alerts</span>
            </TabsTrigger>
            <TabsTrigger value="sounds" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-lg text-xs sm:text-sm">
              <Volume2 className="h-4 w-4 mr-1 sm:mr-2" />
              Sounds
            </TabsTrigger>
            <TabsTrigger value="storage" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white rounded-lg text-xs sm:text-sm">
              <HardDrive className="h-4 w-4 mr-1 sm:mr-2" />
              Storage
            </TabsTrigger>
            <TabsTrigger value="security" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-lg text-xs sm:text-sm">
              <Shield className="h-4 w-4 mr-1 sm:mr-2" />
              Security
            </TabsTrigger>
          </TabsList>
          
          {/* Notifications Tab */}
          <TabsContent value="notifications" className="mt-6 space-y-6">
            <Card className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
              <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5" />
                  Email & Alert Preferences
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                {/* Admin Email */}
                <div>
                  <Label className="text-slate-700 font-semibold">Admin Email Address</Label>
                  <Input
                    type="email"
                    value={notificationSettings.admin_email}
                    onChange={(e) => setNotificationSettings({
                      ...notificationSettings,
                      admin_email: e.target.value
                    })}
                    placeholder="admin@example.com"
                    className="mt-2"
                  />
                  <p className="text-xs text-slate-500 mt-1">Notifications will be sent to this email address</p>
                </div>
                
                {/* Flight Notification Toggles */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-slate-800 flex items-center gap-2">
                    <Plane className="h-4 w-4 text-blue-600" />
                    Flight Arrival Notifications
                  </h4>
                  
                  <NotificationToggle
                    id="toggle4Days"
                    checked={notificationSettings.enabled_4_days}
                    onChange={(checked) => setNotificationSettings({
                      ...notificationSettings,
                      enabled_4_days: checked
                    })}
                    title="4 Days Before Arrival"
                    description="Get notified 4 days before worker arrival"
                    icon={Clock}
                  />
                  
                  <NotificationToggle
                    id="toggle24Hours"
                    checked={notificationSettings.enabled_24_hours}
                    onChange={(checked) => setNotificationSettings({
                      ...notificationSettings,
                      enabled_24_hours: checked
                    })}
                    title="24 Hours Before Arrival"
                    description="Get notified 1 day before arrival"
                    icon={Clock}
                  />
                  
                  <NotificationToggle
                    id="toggle2Hours"
                    checked={notificationSettings.enabled_2_hours}
                    onChange={(checked) => setNotificationSettings({
                      ...notificationSettings,
                      enabled_2_hours: checked
                    })}
                    title="2 Hours Before Arrival"
                    description="Get urgent alert 2 hours before landing"
                    icon={AlertTriangle}
                  />
                  
                  <NotificationToggle
                    id="toggleLanded"
                    checked={notificationSettings.enabled_landed}
                    onChange={(checked) => setNotificationSettings({
                      ...notificationSettings,
                      enabled_landed: checked
                    })}
                    title="Flight Landed"
                    description="Get notified when flight lands"
                    icon={Plane}
                  />
                  
                  <NotificationToggle
                    id="toggleDelayed"
                    checked={notificationSettings.enabled_delayed}
                    onChange={(checked) => setNotificationSettings({
                      ...notificationSettings,
                      enabled_delayed: checked
                    })}
                    title="Flight Delayed"
                    description="Get notified when flight is delayed"
                    icon={AlertTriangle}
                  />
                </div>
                
                {/* Delay Threshold */}
                <div>
                  <Label className="text-slate-700 font-semibold">Delay Threshold (minutes)</Label>
                  <Input
                    type="number"
                    value={notificationSettings.delay_threshold_minutes}
                    onChange={(e) => setNotificationSettings({
                      ...notificationSettings,
                      delay_threshold_minutes: parseInt(e.target.value) || 30
                    })}
                    min="0"
                    className="mt-2 w-40"
                  />
                  <p className="text-xs text-slate-500 mt-1">Only notify if delay exceeds this many minutes</p>
                </div>
                
                {/* Save Button */}
                <div className="flex gap-3 pt-4 border-t border-slate-200">
                  <Button 
                    onClick={saveNotificationSettings}
                    disabled={saving}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                    Save Settings
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={loadSettings}
                    disabled={loading}
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Reload
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Sounds Tab */}
          <TabsContent value="sounds" className="mt-6 space-y-6">
            <Card className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
              <CardHeader className="bg-gradient-to-r from-purple-600 to-purple-700 text-white">
                <CardTitle className="flex items-center gap-2">
                  <Volume2 className="h-5 w-5" />
                  Sound Preferences
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <NotificationToggle
                  id="toggleWelcomeSound"
                  checked={soundSettings.welcome_sound}
                  onChange={(checked) => setSoundSettings({
                    ...soundSettings,
                    welcome_sound: checked
                  })}
                  title="🔔 Welcome Sound"
                  description="Play subtle airline chime when opening the app"
                  icon={Volume2}
                />
                {/* Test welcome sound button */}
                <div className="pl-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      const { playWelcomeChimePreview } = await import('@/utils/sounds');
                      await playWelcomeChimePreview();
                      toast.success('🎵 Welcome chime played');
                    }}
                    className="text-purple-700 border-purple-200 hover:bg-purple-50"
                    data-testid="test-welcome-sound-btn"
                  >
                    <Volume2 className="h-4 w-4 mr-2" />
                    Test Welcome Sound / اختبار
                  </Button>
                </div>
                
                <NotificationToggle
                  id="toggleNotificationSound"
                  checked={soundSettings.notification_sound}
                  onChange={(checked) => setSoundSettings({
                    ...soundSettings,
                    notification_sound: checked
                  })}
                  title="🔔 Airline Boarding Sound"
                  description="Play boarding chime when new notifications arrive"
                  icon={Bell}
                />
                {/* Test notification sound button */}
                <div className="pl-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      const { playNotificationChimePreview } = await import('@/utils/sounds');
                      await playNotificationChimePreview();
                      toast.success('🔔 Notification chime played');
                    }}
                    className="text-purple-700 border-purple-200 hover:bg-purple-50"
                    data-testid="test-notification-sound-btn"
                  >
                    <Bell className="h-4 w-4 mr-2" />
                    Test Notification Sound / اختبار
                  </Button>
                </div>
                
                {/* Save Button */}
                <div className="pt-4 border-t border-slate-200">
                  <Button 
                    onClick={saveSoundSettings}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save Sound Preferences
                  </Button>
                </div>

                {/* Info box */}
                <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 text-xs text-purple-800">
                  <p className="font-semibold mb-1">💡 ملاحظة هامة</p>
                  <p className="text-purple-700">متصفحات الموبايل (خاصة iOS) تمنع تشغيل الأصوات قبل تفاعل المستخدم. يجب أن تلمس الشاشة مرة واحدة بعد فتح التطبيق حتى تعمل الأصوات.</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Storage Tab */}
          <TabsContent value="storage" className="mt-6 space-y-6">
            <StorageManagementCard saving={saving} setSaving={setSaving} />
          </TabsContent>
          
          {/* Security Tab */}
          <TabsContent value="security" className="mt-6 space-y-6">
            {/* Public Tracking Link */}
            <Card className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
              <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                <CardTitle className="flex items-center gap-2">
                  <Link className="h-5 w-5" />
                  🔗 رابط المتابعة العام / Public Tracker Link
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                {/* Main Tracker Link */}
                <div className="bg-gradient-to-r from-blue-50 to-emerald-50 border-2 border-blue-200 rounded-xl p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-blue-500 to-emerald-500 flex items-center justify-center">
                      <Shield className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-800 text-lg">TADABER Tracker</h4>
                      <p className="text-sm text-slate-600">متابعة الفيزا والجوازات في صفحة واحدة</p>
                    </div>
                    <Badge className="bg-blue-500 ml-auto">PIN Protected</Badge>
                  </div>
                  
                  <p className="text-sm text-slate-600 mb-4">
                    شارك هذا الرابط الموحد مع العملاء لمتابعة حالة الفيزا وتسليم الجوازات في مكان واحد.
                    <br />
                    <span className="text-slate-500">Share this unified link with clients to track visa status and passport delivery in one place.</span>
                  </p>
                  
                  <div className="flex gap-2">
                    <Input
                      value={`${window.location.origin}/tracker`}
                      readOnly
                      className="bg-white font-mono text-sm border-blue-200 flex-1"
                    />
                    <Button
                      onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin}/tracker`);
                        toast.success('تم نسخ الرابط! / Link copied!');
                      }}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      <Copy className="h-4 w-4 mr-2" />
                      نسخ
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => window.open('/tracker', '_blank')}
                      className="border-blue-300 text-blue-600 hover:bg-blue-50"
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      معاينة
                    </Button>
                  </div>
                </div>
                
                {/* Features */}
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                  <h5 className="font-semibold text-emerald-800 mb-2">✨ مميزات الصفحة:</h5>
                  <ul className="text-sm text-emerald-700 space-y-1">
                    <li>✅ متابعة الفيزا وتواريخ الانتهاء</li>
                    <li>✅ متابعة حالة تسليم الجوازات</li>
                    <li>✅ تحديث تلقائي كل 30 ثانية</li>
                    <li>✅ حماية بـ PIN للخصوصية</li>
                    <li>✅ بحث سريع بالاسم أو رقم الجواز</li>
                    <li>✅ تصميم متجاوب للجوال</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
            
            {/* PIN Settings */}
            <Card className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
              <CardHeader className="bg-gradient-to-r from-amber-600 to-amber-700 text-white">
                <CardTitle className="flex items-center gap-2">
                  <Key className="h-5 w-5" />
                  🔐 Visa Tracker PIN / رمز الدخول
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="h-5 w-5 text-amber-600" />
                    <span className="font-semibold text-amber-800">Access Control / التحكم بالوصول</span>
                  </div>
                  <p className="text-sm text-amber-700">
                    The PIN protects the Visa Tracker page from unauthorized access.
                    <br />
                    <span className="text-amber-600">رمز PIN يحمي صفحة متابعة الفيزا من الوصول غير المصرح به</span>
                  </p>
                </div>
                
                {/* Direct PIN Input */}
                <div>
                  <Label className="text-slate-700 font-semibold">New PIN / رمز جديد</Label>
                  <div className="flex gap-2 mt-2">
                    <div className="relative flex-1">
                      <Input
                        type={showNewPin ? 'text' : 'password'}
                        value={newPin}
                        onChange={(e) => setNewPin(e.target.value)}
                        placeholder="Enter new PIN (min 4 characters)"
                        className="pr-10"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8"
                        onClick={() => setShowNewPin(!showNewPin)}
                      >
                        {showNewPin ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                    <Button
                      onClick={handleChangePin}
                      disabled={saving || !newPin || newPin.length < 4}
                      className="bg-amber-600 hover:bg-amber-700"
                    >
                      {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                      Save PIN
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">PIN must be at least 4 characters / يجب أن يكون الرمز 4 أحرف على الأقل</p>
                </div>
              </CardContent>
            </Card>
            
            {/* System Info */}
            <Card className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
              <CardHeader className="bg-gradient-to-r from-slate-600 to-slate-700 text-white">
                <CardTitle className="flex items-center gap-2">
                  <Info className="h-5 w-5" />
                  System Information
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                    <p className="text-xs text-slate-500 uppercase font-semibold mb-1">Version</p>
                    <p className="text-lg font-bold text-slate-800">v3.9</p>
                  </div>
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                    <p className="text-xs text-slate-500 uppercase font-semibold mb-1">Environment</p>
                    <p className="text-lg font-bold text-slate-800">Production</p>
                  </div>
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                    <p className="text-xs text-slate-500 uppercase font-semibold mb-1">Database</p>
                    <p className="text-lg font-bold text-emerald-600 flex items-center gap-2">
                      <Database className="h-4 w-4" />
                      Connected
                    </p>
                  </div>
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                    <p className="text-xs text-slate-500 uppercase font-semibold mb-1">User</p>
                    <p className="text-lg font-bold text-slate-800 flex items-center gap-2">
                      <User className="h-4 w-4" />
                      Admin
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Data Maintenance */}
            <Card className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
              <CardHeader className="bg-gradient-to-r from-orange-600 to-red-600 text-white">
                <CardTitle className="flex items-center gap-2">
                  <Database className="h-5 w-5" />
                  🔧 Data Maintenance / صيانة البيانات
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="h-5 w-5 text-amber-600" />
                    <span className="font-semibold text-amber-800">تحذير / Warning</span>
                  </div>
                  <p className="text-sm text-amber-700">
                    استخدم هذه الأدوات فقط عند وجود مشاكل في البيانات.
                    <br />
                    <span className="text-amber-600">Use these tools only when there are data issues.</span>
                  </p>
                </div>
                
                {/* Recalculate Remaining Amounts */}
                <div className="border border-slate-200 rounded-xl p-4">
                  <h5 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                    <RefreshCw className="h-4 w-4 text-blue-500" />
                    إعادة حساب المبالغ المتبقية / Recalculate Remaining Amounts
                  </h5>
                  <p className="text-sm text-slate-600 mb-4">
                    يعيد حساب المبلغ المتبقي لجميع العمال بناءً على الصيغة الصحيحة:
                    <br />
                    <code className="bg-slate-100 px-2 py-1 rounded text-xs font-mono">
                      Remaining = Total - Paid + Deductions
                    </code>
                  </p>
                  <Button
                    onClick={async () => {
                      if (!window.confirm('هل أنت متأكد من إعادة حساب المبالغ المتبقية لجميع العمال؟\n\nAre you sure you want to recalculate remaining amounts for all workers?')) {
                        return;
                      }
                      try {
                        setSaving(true);
                        const result = await apiPost('/api/cases/recalculate-remaining', {});
                        toast.success(`تم تحديث ${result.updated_workers} عامل من ${result.total_workers} / Updated ${result.updated_workers} workers out of ${result.total_workers}`);
                      } catch (error) {
                        toast.error('فشل إعادة الحساب / Recalculation failed');
                      } finally {
                        setSaving(false);
                      }
                    }}
                    disabled={saving}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                    إعادة حساب / Recalculate
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default SettingsPage;
